import React, { useState, useEffect } from "react";
import { Icon, Popover } from "@clarkinc/zest-design-system";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import GearActive from "./gearactive.svg";
import GearInactive from "./gearinactive.svg";
import Button from "~/common/Buttons/index";
import { InstallationPricing } from "./getInstallationServicePricingData";
import InstallationServiceModal from "./InstallationServiceModal";

const pricingInstallationServicesPopoverId =
  "pricing-installation-services-popover-id";

import(/* webpackChunkName: "YourInformationSectionToolTip" */ "../tippy.css");

export enum ServiceLevelType {
  Both = 1,
  FullService = 2,
  FinalConnections = 3,
}

export enum InstallationServiceType {
  FullService = "fullService",
  FinalConnections = "finalConnections",
}

export interface InstallationServiceSelections {
  includeHaulAway: boolean;
  includeOnSiteSurvey: boolean;
  hasFreightElevator: boolean;
  requireGoingToDifferentFloor: boolean;
  serviceType?: InstallationServiceType;
  stairFlightsQty: number;
}

interface Props {
  quantity: number;
  setInstallationServiceIds: (ids: number[]) => void;
  pricingData: InstallationPricing;
  serviceLevelId: number;
}

const InstallationService = ({
  quantity,
  setInstallationServiceIds,
  pricingData,
  serviceLevelId,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formSelections, setFormSelections] = useState(
    {} as InstallationServiceSelections,
  );

  useEffect(() => {
    setInstallationServiceIds(getSelectedServiceIds(formSelections));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSelections.serviceType]);

  const serviceAdded = !!formSelections.serviceType;

  const getSelectedServiceIds = (selections: InstallationServiceSelections) => {
    const selectedIds: number[] = [];

    if (!selections.serviceType) {
      return selectedIds;
    }
    if (selections.serviceType === InstallationServiceType.FullService) {
      selectedIds.push(pricingData.fullService.serviceTierPricingId);
    } else {
      selectedIds.push(pricingData.finalConnections.serviceTierPricingId);
    }

    if (selections.includeHaulAway) {
      selectedIds.push(pricingData.haulAway.serviceTierPricingId);
    }

    if (
      selections.requireGoingToDifferentFloor &&
      !selections.hasFreightElevator
    ) {
      selectedIds.push(
        selections.stairFlightsQty > 3
          ? pricingData.fourToNinetyNineFlights.serviceTierPricingId
          : pricingData.oneToThreeFlights.serviceTierPricingId,
      );
    }

    if (selections.includeOnSiteSurvey) {
      selectedIds.push(
        selections.serviceType === InstallationServiceType.FullService
          ? pricingData.surveyNoCharge.serviceTierPricingId
          : pricingData.survey.serviceTierPricingId,
      );
    }

    return selectedIds;
  };

  const calculateServiceTotalPrice = (
    selections: InstallationServiceSelections,
  ): number => {
    const selectedIds: number[] = getSelectedServiceIds(selections);

    const services = [
      pricingData.finalConnections,
      pricingData.fourToNinetyNineFlights,
      pricingData.fullService,
      pricingData.haulAway,
      pricingData.oneToThreeFlights,
      pricingData.survey,
      pricingData.surveyNoCharge,
    ];

    const totalPrice = services.reduce(
      (total, service) =>
        selectedIds.includes(service.serviceTierPricingId)
          ? total + service.price
          : total,
      0,
    );

    return totalPrice * quantity;
  };

  const handleAddOrEditEvent = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    setIsModalOpen(true);
  };

  const handleRemoveEvent = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    setFormSelections({} as InstallationServiceSelections);
  };

  return (
    <>
      <div
        className="bg-white border-gray-500 rounded box-border flex items-center flex-1 mx-auto mb-2 py-3 w-full"
        data-testid="installation-service-container"
      >
        {!serviceAdded && (
          <span className="flex-shrink-0 h-10 w-10">
            <img
              alt="Installation unselected icon"
              className="h-10 w-10"
              src={GearInactive}
              data-testid="inactive-gear-image"
            />
          </span>
        )}
        {serviceAdded && (
          <span className="flex-shrink-0 h-10 w-10">
            <img
              alt="Installation selected icon"
              className="h-10 w-10"
              src={GearActive}
              data-testid="active-gear-image"
            />
          </span>
        )}
        <div className="px-2">
          <h4 className="leading-3 mb-1">Installation Service</h4>
          {!serviceAdded && (
            <p className="font-normal text-xs leading-none mb-0 text-left text-gray-800">
              Fast and easy installation by a certified service partner
              <Popover className="px-1 align-sub">
                <Popover.Trigger
                  contentId={pricingInstallationServicesPopoverId}
                >
                  <Icon
                    name="ri-question-fill"
                    aria-label="more information"
                    size="xsmall"
                    className="fill-green-500"
                  />
                </Popover.Trigger>
                <Popover.Content id={pricingInstallationServicesPopoverId}>
                  We offer two levels of installation services for our customers
                  to choose from. The installation service will be coordinated
                  by Ready Kitchen Warranty, and is available throughout the
                  U.S. to commercial locations only.
                </Popover.Content>
              </Popover>
            </p>
          )}
          {serviceAdded && (
            <p className="font-normal text-xs leading-none mb-0 text-left text-gray-800">
              <span id="installNetworkInfoTextSelectedServiceName">
                {`${
                  formSelections.serviceType ===
                  InstallationServiceType.FullService
                    ? "Full Service"
                    : "Final Connections"
                } `}
              </span>
              <a
                href="/"
                target="blank"
                className="text-green-500 underline"
                onClick={handleAddOrEditEvent}
              >
                Edit
              </a>
              &nbsp;|&nbsp;
              <a href="/" className="underline" onClick={handleRemoveEvent}>
                Remove
              </a>
            </p>
          )}
        </div>
        {!serviceAdded && (
          <Button
            type="button"
            color="green-solid"
            size="mini"
            className="flex-shrink-0 ml-auto"
            name="addinstallationservice"
            onClick={handleAddOrEditEvent}
            style={{ width: "110px" }}
          >
            Add Installation
          </Button>
        )}
        {serviceAdded && (
          <p
            className="font-semibold text-sm mb-0 ml-auto text-red-500"
            id="installNetworkSelectedServicePriceDisplay"
          >
            {formatAsCurrency.formatIfUndefined(
              calculateServiceTotalPrice(formSelections),
            )}
          </p>
        )}
      </div>
      {pricingData && (
        <InstallationServiceModal
          serviceLevelId={serviceLevelId}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          quantity={quantity}
          pricingData={pricingData}
          calculateServiceTotalPrice={calculateServiceTotalPrice}
          formSelections={formSelections}
          setFormSelections={setFormSelections}
          key={formSelections.serviceType}
        />
      )}
    </>
  );
};

export default InstallationService;
